import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { ListItemText, MenuItem, makeStyles } from '@material-ui/core';
import FormSelectField from '~/modules/common/components/FormSelectField';
import { CostOverrideType } from '../../CostOverrideType';

const useStyles = makeStyles(theme => ({
  menuItem: {
    whiteSpace: 'normal'
  },
  secondary: {
    color: theme.palette.text.secondary
  },
  select: {
    textAlign: 'left',
    paddingTop: theme.spacing(1.75),
    paddingBottom: theme.spacing(1.75)
  }
}));

export const CostOverrideTypeSelector = ({
  value,
  onChange,
  disabled = false,
  dataQeId
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const secondaryTypographyProps = useMemo(
    () => ({ className: classes.secondary }),
    [classes.secondary]
  );
  const selectClasses = useMemo(() => ({ select: classes.select }), [
    classes.select
  ]);

  const options = useMemo(
    () => [
      {
        value: CostOverrideType.HOURLY_RATE,
        label: formatMessage({ id: 'projectCostOverrides.type.hourlyRate' }),
        description: formatMessage({
          id: 'projectCostOverrides.type.hourlyRateDescription'
        })
      },
      {
        value: CostOverrideType.PERCENT_MARKUP,
        label: formatMessage({ id: 'projectCostOverrides.type.percentMarkup' }),
        description: formatMessage({
          id: 'projectCostOverrides.type.percentMarkupDescription'
        })
      }
    ],
    [formatMessage]
  );

  const handleChange = useCallback(
    event => {
      onChange(event.target.value);
    },
    [onChange]
  );

  const renderValue = useCallback(
    selected => options.find(o => o.value === selected)?.label ?? '',
    [options]
  );

  return (
    <FormSelectField
      variant="filled"
      value={value}
      onChange={handleChange}
      editable={!disabled}
      renderValue={renderValue}
      classes={selectClasses}
      dataQeId={dataQeId}
    >
      {options.map(option => (
        <MenuItem
          key={option.value}
          value={option.value}
          className={classes.menuItem}
        >
          <ListItemText
            primary={option.label}
            secondary={option.description}
            secondaryTypographyProps={secondaryTypographyProps}
          />
        </MenuItem>
      ))}
    </FormSelectField>
  );
};

CostOverrideTypeSelector.propTypes = {
  value: PropTypes.oneOf([
    CostOverrideType.HOURLY_RATE,
    CostOverrideType.PERCENT_MARKUP
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  dataQeId: PropTypes.string
};

export default CostOverrideTypeSelector;
